import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { withSize } from 'react-sizeme';
import classNames from 'classnames';

import { Sizes } from '@ratehub/base-ui';


function CardFooter({ columns, size, className, ...otherProps }) {
    // only supporting column reversal when 1st column is empty and 2nd not; ignores columns 3 & 4
    const reverseColumnOrder = size.width < MIN_WIDTH_SINGLE_ROW && !columns[0] && columns[1];

    // typically four columns (2 pairs of 2), we may also squeeze in a fifth (in 3 and 2 columns)
    const hasFiveColumns = columns.length === 5;

    return (
        <div
            {...otherProps}
            className={classNames(
                className,
                'rh-display-flex', 'rh-flex-wrap', 'rh-pt-1_5', 'rh-px-1_75',
                'rh-justify-content-space-between', 'rh-box-sizing-border-box', {
                    'divide-columns-into-thirds': hasFiveColumns,
                },
            )}
        >
            <div className={classNames('column-wrapper rh-display-flex rh-flex-wrap', {
                reverseColumnOrder: reverseColumnOrder })}
            >
                <FooterColumn>
                    {columns[0]}
                </FooterColumn>

                <FooterColumn>
                    {columns[1]}
                </FooterColumn>

                <If condition={hasFiveColumns}>
                    <FooterColumn>
                        {columns[2]}
                    </FooterColumn>
                </If>
            </div>

            <div className="column-wrapper rh-display-flex rh-flex-wrap">
                <If condition={!hasFiveColumns}>
                    <FooterColumn>
                        {columns[2]}
                    </FooterColumn>
                </If>

                <FooterColumn>
                    {columns[3]}
                </FooterColumn>

                <If condition={hasFiveColumns}>
                    <FooterColumn>
                        {columns[4]}
                    </FooterColumn>

                    {/* placeholder content for when showing five columns; always empty */}
                    <FooterColumn />
                </If>
            </div>
        </div>
    );
}

CardFooter.propTypes = {
    columns: PropTypes.array.isRequired,
    size: PropTypes.shape({
        width: PropTypes.number,
    }).isRequired,
    className: PropTypes.string,
};


const MAX_CONTENT_WIDTH = '1220px';
const CARD_HORIZONTAL_PADDING = Sizes.SPACING.THREE;
const MIN_WIDTH_COLUMN = 200;
const MIN_WIDTH_SINGLE_ROW = 4 * MIN_WIDTH_COLUMN;  // even though we could have five columns


function FooterColumn({
    children,
    isSmallerText = false,
}) {
    return (
        <p
            className={classNames(
                'column-text rh-pr-0_5 rh-mt-0 rh-mx-0 leading-m rh-box-sizing-border-box', {
                    'rh-text-s': !isSmallerText,
                    'rh-text-xs': isSmallerText,
                    'rh-mb-1': children,
                    'rh-mb-0': !children,
                },
            )}
        >
            {children}
        </p>
    );
}

FooterColumn.propTypes = {
    children: PropTypes.node,
    isSmallerText: PropTypes.bool,
};


export default withSize()(observer(styled(CardFooter)`
    justify-content: space-between;
    max-width: calc(${MAX_CONTENT_WIDTH} - (2 * ${CARD_HORIZONTAL_PADDING}));

    > .column-wrapper {
        flex: 1 1 auto;
        justify-content: space-between;

        &.reverseColumnOrder {
            flex-direction: row-reverse;  // only applicable to the first of the two columns
        }

        > .column-text {
            flex: 1 1 50%;  // optionally overridden below based on class name on outer container
            min-width: ${MIN_WIDTH_COLUMN}px;
        }
    }

    &.divide-columns-into-thirds p.column-text {
        flex-basis: 33%;
    }

    &.summer-2023 {
        > .column-wrapper {
            > .column-text {
                flex: 1 1 25%;
                min-width: unset;
            }
        }
    }
`));
